// useOverviewStats — A-019: extraherat från OverviewDialog.tsx.
//
// Samlar de 4 derived-values som alla tab-views (Flat/Detailed/Tree/
// CompactGrid) använder: groupIds-set, crossGroupConnectors-filter,
// resolveName(id)-helper, resolveColor(id)-helper. Pure (läser bara
// props) och memoiserad så onödiga re-renders undviks.

import { useCallback, useMemo } from 'react';
import type { WorkflowBlock, Connector, BlockGroup } from '@/types/workflow';

export interface UseOverviewStatsInput {
  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: BlockGroup[];
}

export interface UseOverviewStatsResult {
  /** Set av alla group-id:n för snabb .has-lookup. */
  groupIds: Set<string>;
  /** Connectors där minst ena änden är en group (inte ett block). */
  crossGroupConnectors: Connector[];
  /** Upp-slag från id → display-name. Hanterar både groups och blocks. */
  resolveName: (id: string) => string;
  /** Upp-slag från id → color. Hanterar både groups och blocks. */
  resolveColor: (id: string) => string;
  /** Shortcut: true om både groups och blocks är tomma. */
  isEmpty: boolean;
}

export function useOverviewStats({
  blocks, connectors, groups,
}: UseOverviewStatsInput): UseOverviewStatsResult {
  const groupIds = useMemo(() => new Set(groups.map((g) => g.id)), [groups]);

  const crossGroupConnectors = useMemo(() => {
    return connectors.filter((c) => groupIds.has(c.fromId) || groupIds.has(c.toId));
  }, [connectors, groupIds]);

  const resolveName = useCallback(
    (id: string) => {
      const group = groups.find((g) => g.id === id);
      if (group) return group.name || 'Group';
      const block = blocks.find((b) => b.id === id);
      if (block) return block.config.name;
      return id;
    },
    [groups, blocks],
  );

  const resolveColor = useCallback(
    (id: string) => {
      const group = groups.find((g) => g.id === id);
      if (group) return group.color;
      const block = blocks.find((b) => b.id === id);
      if (block) return block.color;
      return 'currentColor';
    },
    [groups, blocks],
  );

  const isEmpty = groups.length === 0 && blocks.length === 0;

  return { groupIds, crossGroupConnectors, resolveName, resolveColor, isEmpty };
}
